import React, { useEffect, useState } from 'react';
import { RevisionDiff } from './RevisionDiff';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useRefresh, useRecordContext } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  courseNumber,
  revisionApi,
  reviewStatusLabel,
} from './courseRevisions';

// Inputs can register empty values during initialization without a user edit.
const normalizedValue = (value) => {
  if (value == null || value === '' || (typeof value === 'string' && /^(?:<p>(?:<br\s*\/?>(?:\s*)|&nbsp;|\s)*<\/p>)+$/.test(value))) return '';
  if (Array.isArray(value)) return value.map(normalizedValue);
  if (typeof value === 'object') return Object.fromEntries(
    Object.keys(value).sort().map(key => [key, normalizedValue(value[key])])
  );
  return value;
};

export const hasCourseEdits = (dirtyFields, values, defaults) => {
  if (dirtyFields === true) return JSON.stringify(normalizedValue(values)) !== JSON.stringify(normalizedValue(defaults));
  if (!dirtyFields || typeof dirtyFields !== 'object') return false;
  return Object.keys(dirtyFields).some(key => hasCourseEdits(dirtyFields[key], values?.[key], defaults?.[key]));
};

const FormRevisionPanel = (props) => {
  const { dirtyFields, defaultValues } = useFormState();
  const values = useWatch();
  return <RevisionPanel {...props} dirty={hasCourseEdits(dirtyFields, values, defaultValues)} />;
};
export const CourseRevisionPanel = ({ mode = 'proposals', ...props }) => {
  const form = useFormContext();
  return form ? <FormRevisionPanel {...props} mode={mode} /> : <RevisionPanel {...props} mode={mode} />;
};

export const CourseHistoryPanel = (props) => (
  <Box component="details" sx={{ width: '100%', mt: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
    <Box component="summary" sx={{ p: 2, cursor: 'pointer', fontWeight: 600 }}>Course change history</Box>
    <Box sx={{ px: 2 }}><CourseRevisionPanel {...props} mode="history" /></Box>
  </Box>
);

export const RevisionPanel = ({ courseId, dirty = false, mode = 'all', onLockedFields }) => {
  const refresh = useRefresh();
  const courseRecord = useRecordContext();
  const [record, setRecord] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const id = courseNumber(courseId);
  const load = (data) => {
    const revisions = data.revisions.filter(r => mode === 'all' || (mode === 'history' ? r.status === 'approved' : r.status !== 'approved'));
    onLockedFields?.(data.revisions.filter(r => r.status === 'review').flatMap(r =>
      Object.entries(r.content).flatMap(([locale, fields]) => Object.keys(fields)
        .filter(field => fields[field] !== r.baseline[locale]?.[field])
        .map(field => `${locale}.${field}`))));
    setRecord({ ...data, revisions });
    setSelected(mode === 'history' ? null : revisions.find(r => r.status === 'review') || revisions[0] || null);
  };
  useEffect(() => {
    let active = true;
    onLockedFields?.(null);
    setRecord(null);
    setError('');
    setMessage('');
    revisionApi(`/courses/${id}`)
      .then((data) => {
        if (active) load(data);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [id, courseRecord, mode]);
  const act = async (action, revision = selected) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const data = await revisionApi(
        `/courses/${id}/revisions/${revision.id}/review`,
        { action }
      );
      load(data);
      refresh();
      setMessage(
        action === 'reject'
          ? 'Proposal rejected and deleted. The live course is unchanged.'
          : 'Review saved. The author, reviewer and comparison remain in course history.'
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const latest = record?.revisions[0];
  const pending = record?.revisions.find(r => r.status === 'review');
  const changes = [];
  if (selected)
    Object.entries(selected.content).forEach(([locale, fields]) => {
      Object.entries(fields).forEach(([field, value]) => {
        if (value !== selected.baseline[locale]?.[field])
          changes.push({
            locale,
            field,
            before: selected.baseline[locale]?.[field],
            after: value,
          });
      });
    });
  const comparison = selected && (
            <>
              <Typography variant="subtitle1" mt={2}>
                Changes in version #{selected.id}
              </Typography>
              <Table size="small" aria-label="Before and after comparison">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell colSpan={2}>Changes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((c) => (
                    <TableRow key={`${c.locale}.${c.field}`}>
                      <TableCell>
                        {c.locale} / {c.field}
                      </TableCell>
                      <TableCell colSpan={2} sx={{ verticalAlign: 'top', width: '85%' }}>
                        <RevisionDiff key={selected.id} before={c.before} after={c.after} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!changes.length && (
                    <TableRow>
                      <TableCell colSpan={3}>No content differences.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
  );
  if (mode === 'proposals' && !record?.revisions.length && !error && !message) return null;

  return (
    <Box sx={{ mb: 3, width: '100%' }}>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {pending && (
        <Alert severity="warning">
          Needs review — {pending.author_name} saved changes on{' '}
          {pending.created_at} (UTC). The live course is unchanged.
        </Alert>
      )}
      {record && !latest && mode === 'history' && <Typography>No approved changes yet.</Typography>}
      {record && (
        <>
          {[
            { title: 'Proposed changes', rows: record.revisions.filter(r => r.status !== 'approved') },
            { title: 'Course change history', rows: record.revisions.filter(r => r.status === 'approved') },
          ].filter(group => group.title !== 'Proposed changes' || group.rows.length > 0).filter(group => mode === 'all' || (mode === 'history' ? group.title === 'Course change history' : group.title === 'Proposed changes')).map(group => <Box key={group.title} sx={{ mb: 3 }}>
            {mode !== 'history' && <Typography variant="h6">{group.title}</Typography>}
          <Table size="small" aria-label={group.title}>
            <TableHead>
              <TableRow>
                {[
                  'Version',
                  'Changed by / date (UTC)',
                  'Status',
                  'Reviewed by / date (UTC)',
                  '',
                ].map((t) => (
                  <TableCell key={t}>{t}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {group.rows.map((r) => (
                <React.Fragment key={r.id}>
                <TableRow selected={selected?.id === r.id}>
                  <TableCell>#{r.id}</TableCell>
                  <TableCell>
                    {r.author_name}
                    <br />
                    {r.created_at}
                  </TableCell>
                  <TableCell>{reviewStatusLabel(r.status)}</TableCell>
                  <TableCell>
                    {r.reviewer_name}
                    <br />
                    {r.reviewed_at}
                    <br />
                    {r.sync_status === 'failed' ? 'Publication failed' : ''}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      disabled={busy}
                      aria-expanded={selected?.id === r.id}
                      aria-controls={selected?.id === r.id ? 'revision-comparison-' + r.id : undefined}
                      onClick={() => setSelected(selected?.id === r.id ? null : r)}
                    >
                      Compare #{r.id}
                    </Button>
                  </TableCell>
                </TableRow>
                {selected?.id === r.id && (
                  <TableRow id={'revision-comparison-' + r.id}>
                    <TableCell colSpan={5} sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      {comparison}
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
            {!group.rows.length && <Typography color="text.secondary" sx={{ p: 1 }}>No entries.</Typography>}
          </Box>)}
          {record.canReview &&
            selected?.id === pending?.id &&
            selected?.status === 'review' && (
              <>
                {dirty && (
                  <Alert severity="info">
                    Save or discard your course edits before reviewing a
                    version.
                  </Alert>
                )}
                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="contained"
                    disabled={busy || dirty}
                    onClick={() => act('approve')}
                  >
                    Approve changes
                  </Button>
                  <Button
                    type="button"
                    disabled={busy || dirty}
                    onClick={() => act('reject')}
                  >
                    Reject changes
                  </Button>
                </Stack>
              </>
            )}
          {record.canReview &&
            selected?.status === 'approved' &&
            ['pending', 'failed'].includes(selected.sync_status) && (
              <Alert severity="warning">
                Website publication is not confirmed.{' '}
                <Button
                  type="button"
                  disabled={busy || dirty}
                  onClick={() => act('retry')}
                >
                  Retry publication
                </Button>
              </Alert>
            )}
        </>
      )}
    </Box>
  );
};
